import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Menu,
  MenuItem,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
} from "@mui/material";

import AppsIcon from "@mui/icons-material/Apps";
import EmailIcon from "@mui/icons-material/Email";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SendIcon from "@mui/icons-material/Send";
//import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import DeleteIcon from "@mui/icons-material/Delete";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import EditIcon from "@mui/icons-material/Edit";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import FileDialog from "components/FileDialog";

import useUserRoles from "hooks/useUserRoles";
import { getCommunicationStatus, getErrorDetails } from "utils/comunicacionStatus";

import ComunicacionesApi from "api/comunicacionesApi";
import BucketApi from "api/bucketApi";
import DialogDestinatarios from "./dialogDestinatarios";
import NotificarComunicacion from "./notificarComunicacion";
import EditComunicacion from "../../comunicaciones/components/editComunicacion";

const CardComunicaciones = ({ proceso, onProcesoChange }) => {
  const { user, roles } = useUserRoles();

  /**
   * Parsea una fecha en formato "YYYY-MM-DD" a Date local sin problemas de timezone.
   * Evita el problema de interpretación UTC que causa que las fechas se muestren un día antes.
   *
   * @param {string} dateString - Fecha en formato "YYYY-MM-DD"
   * @returns {Date|null} - Objeto Date en hora local o null si no es válida
   */
  const parseDateLocal = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day); // month - 1 porque los meses son 0-indexed en JS
  };

  const [comunicaciones, setComunicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogNotificar, setOpenDialogNotificar] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedComunicacion, setSelectedComunicacion] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Estados para manejar PDFs y comprobantes
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfTitle, setPdfTitle] = useState("");
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [tipoArchivo, setTipoArchivo] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);

  const fetchComunicaciones = async () => {
    try {
      setLoading(true);
      const response = await ComunicacionesApi.obtenerComunicacionesProcesoSoft(proceso.id);
      setComunicaciones(response.data);
    } catch (error) {
      console.error("Error obteniendo comunicaciones:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (proceso && proceso.id) {
      fetchComunicaciones();
    }
  }, [user, proceso]);

  const handleNotificacionSuccess = async () => {
    try {
      await fetchComunicaciones();
      if (onProcesoChange) {
        onProcesoChange();
      }
      setOpenDialogNotificar(false);
      setSelectedComunicacion(null);
    } catch (error) {
      console.error("Error actualizando comunicaciones después de notificación:", error);
    }
  };

  // Verifica si todos los destinatarios de la comunicación han sido notificados
  const isFullyNotified = (comunicacion) => {
    if (!comunicacion || !comunicacion.destinatarios) return false;
    const { destinatarios } = comunicacion;
    // Para comunicaciones físicas
    if (comunicacion.tipo_comunicacion === "FISICA") {
      if (destinatarios.acreedor) {
        return destinatarios.acreedor.notificado === true;
      }
      return false;
    }
    // Para comunicaciones por email
    // Verificar deudor
    if (destinatarios.deudor && !destinatarios.deudor.notificado) return false;
    // Verificar apoderado del deudor
    if (destinatarios.deudor_apoderado && !destinatarios.deudor_apoderado.notificado) return false;
    // Verificar centrales de riesgo
    if (destinatarios.centrales_riesgo && destinatarios.centrales_riesgo.length > 0) {
      if (!destinatarios.centrales_riesgo.every((central) => central.notificado)) return false;
    }
    // Verificar acreedores
    if (destinatarios.acreedores && destinatarios.acreedores.length > 0) {
      if (!destinatarios.acreedores.every((acreedor) => acreedor.notificado)) return false;
    }
    return true;
  };

  const handleActualizarDestinatarios = async (comunicacion) => {
    try {
      const response = await ComunicacionesApi.obtenerDestinatarios(comunicacion.id);
      if (!response.data) {
        alert("No se pudieron obtener los destinatarios actualizados");
        return;
      }
      const confirmAction = window.confirm(
        `¿Estás seguro(a) que deseas actualizar los Destinatarios de esta Comunicación?\n\n` +
          `Proceso: ${comunicacion.proceso_identificador}\n` +
          `Asunto: ${comunicacion.asunto}`
      );
      if (!confirmAction) return;
      const dataToUpdate = {
        proceso: comunicacion.proceso_id,
        audiencia: comunicacion.audiencia_id,
        tipo_comunicacion: comunicacion.tipo_comunicacion,
        formato_comunicacion: comunicacion.formato_comunicacion,
        asunto: comunicacion.asunto,
        contenido: comunicacion.contenido || "",
        destinatarios: response.data, // Nuevos destinatarios
        creado_por: comunicacion.creado_por,
      };
      try {
        setLoading(true);
        await ComunicacionesApi.actualizarComunicacion(comunicacion.id, dataToUpdate);
        alert("Destinatarios actualizados correctamente");
        fetchComunicaciones();
      } catch (error) {
        console.error("Error actualizando los destinatarios:", error);
        alert(obtenerMensajeError(error, "Error al actualizar los destinatarios"));
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error al actualizar destinatarios:", error);
      // Mensaje de error más descriptivo
      const errorMsg = error.response?.data?.error || error.message || "Error desconocido";
      alert(`Error al actualizar los Destinatarios:\n${errorMsg}`);
    }
  };

  const handleOpenDialog = (comunicacion) => {
    setSelectedComunicacion(comunicacion);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedComunicacion(null);
  };
  const handleOpenNotificar = (comunicacion) => {
    setSelectedComunicacion(comunicacion);
    setOpenDialogNotificar(true);
  };
  const handleCloseDialogNotificar = () => {
    setOpenDialogNotificar(false);
    setSelectedComunicacion(null);
    if (onProcesoChange) {
      onProcesoChange();
    }
  };
  const handleOpenEdit = (comunicacion) => {
    setSelectedComunicacion(comunicacion);
    setOpenEditDialog(true);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };
  const handleOpenUploadDialog = (comunicacion) => {
    setSelectedComunicacion(comunicacion);
    setOpenUploadDialog(true);
  };

  // Función para manejar el cambio de archivo
  const handleFileChange = (event) => {
    setUploadFile(event.target.files[0]);
  };

  const handleUploadArchivo = async () => {
    if (tipoArchivo == "") {
      alert("Seleccione un Tipo de Archivo");
      return;
    }
    if (!uploadFile) {
      alert("Seleccione un Archivo");
      return;
    }
    let allowedExtensions = ["pdf"];
    const extension = uploadFile.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      alert("Sólo se pueden anexar archivos con extensión PDF.");
      return;
    }
    try {
      setFileUploading(true);
      let fileName = `${tipoArchivo}.${extension}`;
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("file_name", fileName);
      formData.append("file_type", "comunicaciones");
      formData.append("proceso_id", proceso.identificador);
      formData.append("comunicacion_id", selectedComunicacion.id);
      const response = await BucketApi.uploadFile(formData);
      if (response.data && response.data.file_url) {
        if (
          tipoArchivo == "comprobante_envio" &&
          selectedComunicacion.tipo_comunicacion == "FISICA"
        ) {
          const destinatariosActualizados = selectedComunicacion.destinatarios.map((d) => ({
            ...d,
            notificado: true,
          }));
          const destinatariosSeleccionados = {};
          destinatariosActualizados.forEach((_, idx) => {
            destinatariosSeleccionados[`acreedor_${idx}`] = true;
          });
          const datos = {
            operacion: "NOTIFICAR_COMUNICACION",
            id_comunicacion: selectedComunicacion.id,
            destinatarios_seleccionados: destinatariosSeleccionados,
          };
          await ComunicacionesApi.operarComunicacion(datos);
        }
        alert("Archivo subido correctamente.");
        fetchComunicaciones();
        setOpenUploadDialog(false);
        setUploadFile(null);
      } else {
        throw new Error("No se pudo obtener la URL del archivo subido");
      }
    } catch (error) {
      console.error("Error al subir el Archivo:", error);
      alert(obtenerMensajeError(error, "Error al subir el Archivo. Intente nuevamente."));
    } finally {
      setFileUploading(false);
    }
  };

  const handleViewPdf = async (tipo_archivo, comunicacion) => {
    if (tipo_archivo == "comprobante_envio") {
      setPdfTitle(`Comprobante de Envío`);
    } else {
      setPdfTitle(`Anexo`);
    }
    let fileName = `${tipo_archivo}.pdf`;
    let urlResponse = await BucketApi.getComunicacionFileUrlFresh(
      fileName,
      comunicacion.proceso_identificador,
      comunicacion.id
    );
    let newUrl = urlResponse.data.file_url;
    if (urlResponse.data.exists) {
      setPdfUrl(newUrl);
    }
    if (!newUrl) {
      alert("El PDF no ha sido cargado.");
    }
    setShowPdfViewer(true);
    handleMenuClose();
    return;
  };

  const handleViewPdfNotificacion = async (comunicacion) => {
    if (!comunicacion) return;
    // Limpiar URL anterior si existe
    if (pdfUrl && pdfUrl.startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
    }
    try {
      setLoading(true);
      const timestamp = Date.now();
      const pdfResponse = await ComunicacionesApi.generateComunicacionPdf(
        comunicacion.id,
        { responseType: "blob" },
        timestamp
      );
      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      // Crear una URL única forzando re-render
      const uniqueUrl = `${url}#${timestamp}`;
      setPdfTitle(`Notificación - ${comunicacion.formato_comunicacion}`);
      setPdfUrl(uniqueUrl);
      setShowPdfViewer(true);
    } catch (error) {
      console.error("Error generando PDF de notificación:", error);
      alert(obtenerMensajeError(error, "Error al generar el PDF de notificación"));
    } finally {
      setLoading(false);
    }
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro(a) de eliminar esta Comunicación?")) {
      try {
        await ComunicacionesApi.eliminarComunicacion(id);
        fetchComunicaciones();
      } catch (error) {
        console.error("Error al eliminar la Comunicación:", error);
      }
    }
    handleMenuClose();
  };

  // Componente para el menú de opciones según tipo de comunicación
  const renderGridActions = (params) => {
    const comunicacion = params.row;
    //console.log(">>> formato_comunicacion:", `"${comunicacion.formato_comunicacion}"`);
    return (
      <>
        <Box display="flex" flexWrap="wrap" gap={0.5} maxWidth={150}>
          {/* Primera fila de botones */}
          {comunicacion.formato_comunicacion !== "notificacionManual" && (
            <Tooltip title="Actualizar Destinatarios">
              <IconButton
                size="small"
                onClick={() => handleActualizarDestinatarios(comunicacion)}
                color="info"
              >
                <AutorenewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Ver Destinatarios">
            <IconButton size="small" onClick={() => handleOpenDialog(comunicacion)} color="info">
              <AlternateEmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar Comunicación">
            <IconButton size="small" onClick={() => handleDelete(comunicacion.id)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {comunicacion.formato_comunicacion === "notificacionManual" && (
            <Tooltip title="Editar Comunicación">
              <IconButton size="small" onClick={() => handleOpenEdit(comunicacion)} color="primary">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Subir Archivo">
            <IconButton
              size="small"
              onClick={() => handleOpenUploadDialog(comunicacion)}
              color="info"
            >
              <UploadFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={0.5} maxWidth={150}>
          {/* segunda fila */}
          {comunicacion.tipo_comunicacion === "FISICA" && (
            <Tooltip title="Ver Comprobante Envío">
              <IconButton
                size="small"
                onClick={() => handleViewPdf("comprobante_envio", comunicacion)}
                color="primary"
              >
                <MarkEmailReadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Ver Anexo">
            <IconButton
              size="small"
              onClick={() => handleViewPdf("anexo_correo", comunicacion)}
              color="primary"
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver Notificación">
            <IconButton
              size="small"
              onClick={() => handleViewPdfNotificacion(comunicacion)}
              color="success"
            >
              <FilePresentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {comunicacion.tipo_comunicacion === "EMAIL" && (
            <Tooltip title="Notificar Comunicación">
              <IconButton
                size="small"
                onClick={() => handleOpenNotificar(comunicacion)}
                color="secondary"
              >
                <ReplyRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </>
    );
  };

  const columns = [
    {
      field: "Opciones",
      renderHeader: () => <AppsIcon />,
      width: 150,
      sortable: false,
      align: "center",
      disableColumnMenu: true,
      resizable: false,
      renderCell: renderGridActions,
    },
    {
      field: "cliente",
      headerName: "Deudor - Proceso",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.cliente_nombre || ""} - {params.row.proceso_identificador || ""}
        </SuiTypography>
      ),
    },
    {
      field: "asunto",
      headerName: "Asunto",
      flex: 4,
      renderCell: (params) => (
        <>
          <div>
            <Box display="center" alignItems="center">
              <Tooltip title={params.row.tipo_comunicacion}>
                {params.row.tipo_comunicacion === "EMAIL" ? (
                  <EmailIcon color="info" fontSize="medium" sx={{ mr: 1 }} />
                ) : (
                  <LocalShippingIcon color="primary" fontSize="medium" sx={{ mr: 1 }} />
                )}
              </Tooltip>
              <SuiTypography variant="caption" fontWeight="medium" component="span">
                {params.row.asunto}
              </SuiTypography>
              <br />
              <span style={{ fontSize: "90%", marginLeft: "4px", color: "grey" }}>
                ({params.row.formato_comunicacion})
              </span>
            </Box>
          </div>
        </>
      ),
    },
    {
      field: "alarma_notificacion",
      headerName: "Notificación",
      width: 120,
      //flex: 1,
      sortable: true,
      align: "center",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => {
        const comunicacion = params.row;
        const estadoNotificacion = comunicacion.estado_notificacion;
        const diasParaNotificar = comunicacion.dias_para_notificar;
        const fechaAudiencia = comunicacion.fecha_audiencia;

        // Si no hay audiencia vinculada
        if (estadoNotificacion === "SIN_AUDIENCIA") {
          return (
            <Tooltip title="Sin audiencia asociada">
              <Box display="flex" alignItems="center" justifyContent="center">
                <EventBusyIcon sx={{ color: "#9e9e9e", fontSize: 30 }} />
                <span style={{ marginLeft: 4, fontSize: "1rem", color: "#9e9e9e" }}>N/A</span>
              </Box>
            </Tooltip>
          );
        }

        // Configuración de estilos por estado
        const estadoConfig = {
          VENCIDO: {
            icon: <ErrorIcon sx={{ color: "#f44336" }} />,
            bgColor: "#ffebee",
            textColor: "#c62828",
            texto: `${Math.abs(diasParaNotificar)} días vencida`,
            tooltip: `Ya pasó la fecha límite de notificación`,
          },
          CRITICO: {
            icon: <WarningIcon sx={{ color: "#ff5722" }} />,
            bgColor: "#fbe9e7",
            textColor: "#d84315",
            texto: "HOY",
            tooltip: "¡Último día para notificar!",
          },
          URGENTE: {
            icon: <WarningIcon sx={{ color: "#ff9800" }} />,
            bgColor: "#fff3e0",
            textColor: "#ef6c00",
            texto: `${diasParaNotificar} días`,
            tooltip: `Faltan ${diasParaNotificar} días para notificar`,
          },
          ADVERTENCIA: {
            icon: <AccessTimeIcon sx={{ color: "#ffc107" }} />,
            bgColor: "#fffde7",
            textColor: "#f57f17",
            texto: `${diasParaNotificar} días`,
            tooltip: `Faltan ${diasParaNotificar} días para notificar`,
          },
          NORMAL: {
            icon: <CheckCircleIcon sx={{ color: "#4caf50" }} />,
            bgColor: "#e8f5e8",
            textColor: "#2e7d32",
            texto: `${diasParaNotificar} días`,
            tooltip: `Faltan ${diasParaNotificar} días para notificar`,
          },
        };
        const config = estadoConfig[estadoNotificacion];
        if (!config) return null;
        return (
          <Tooltip
            title={`${config.tooltip}${
              fechaAudiencia
                ? ` - Audiencia: ${parseDateLocal(fechaAudiencia).toLocaleDateString("es-ES")}`
                : ""
            }`}
            arrow
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                backgroundColor: config.bgColor,
                color: config.textColor,
                padding: "2px 4px",
                borderRadius: "8px",
                minWidth: "50px",
                border:
                  estadoNotificacion === "VENCIDO" || estadoNotificacion === "CRITICO"
                    ? "1px solid currentColor"
                    : "none",
              }}
            >
              {config.icon}
              <span
                style={{
                  marginLeft: 3,
                  fontSize: "0.75rem",
                  fontWeight:
                    estadoNotificacion === "VENCIDO" || estadoNotificacion === "CRITICO"
                      ? "bold"
                      : "medium",
                }}
              >
                {config.texto}
              </span>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "estado",
      headerName: "Estado",
      flex: 2,
      renderCell: (params) => {
        const comunicacion = params.row;
        let estado = comunicacion.estado || "PENDIENTE";
        // Si todos los destinatarios están notificados, mostrar como ENVIADO/HECHO
        if (isFullyNotified(comunicacion)) {
          estado = comunicacion.tipo_comunicacion === "FISICA" ? "ENVIADO" : "HECHO";
        }
        const estadoStyles = {
          PENDIENTE: { backgroundColor: "#9e9e9e", color: "white" },
          ENVIADO: { backgroundColor: "#43A047", color: "white" },
          GENERADO: { backgroundColor: "#FB8C00", color: "white" },
          ERROR: { backgroundColor: "#E53935", color: "white" },
          HECHO: { backgroundColor: "#43A047", color: "white" },
          PARCIAL: { backgroundColor: "#FB8C00", color: "white" },
        };
        const style = estadoStyles[estado] || { backgroundColor: "gray", color: "white" };
        return (
          <Tooltip title={estado}>
            <div
              style={{
                display: "inline-block",
                backgroundColor: style.backgroundColor,
                color: style.color,
                padding: "4px 12px",
                borderRadius: "12px",
                fontWeight: "bold",
                fontSize: "0.7rem",
                textAlign: "center",
                minWidth: "80px",
              }}
            >
              {estado}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "email_status",
      headerName: "Email",
      width: 100,
      sortable: false,
      align: "center",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => {
        const comunicacion = params.row;
        // Solo mostrar para comunicaciones EMAIL
        if (comunicacion.tipo_comunicacion !== "EMAIL") {
          return <span style={{ color: "#9e9e9e" }}>N/A</span>;
        }
        const status = getCommunicationStatus(comunicacion);
        const errors = getErrorDetails(comunicacion.errores_envio || []);
        let tooltipText = status.tooltip;
        if (errors.length > 0) {
          tooltipText +=
            "\n\nErrores:\n" + errors.map((e) => `• ${e.email}: ${e.descripcion}`).join("\n");
        }
        return (
          <Tooltip title={tooltipText} arrow>
            <span
              style={{
                fontSize: "20px",
                color: status.color,
                cursor: "help",
              }}
            >
              {status.icon}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "fecha_creacion",
      headerName: "Creación",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.fecha_creacion
            ? new Date(params.row.fecha_creacion).toLocaleString("es-ES", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "N/A"}
        </SuiTypography>
      ),
    },
    {
      field: "fecha_envio",
      headerName: "Envío",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.fecha_envio
            ? new Date(params.row.fecha_envio).toLocaleString("es-ES", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "Pendiente"}
        </SuiTypography>
      ),
    },
  ];

  return (
    <SuiBox sx={{ mt: -2, ml: -4, mr: -4 }}>
      <DataGrid
        rows={comunicaciones}
        columns={columns}
        loading={loading}
        getRowHeight={() => "auto"}
        autoHeight
        sx={{
          border: 0,
          backgroundColor: "white",
          borderRadius: 2,
          p: 2,
          [`& .${gridClasses.cell}`]: {
            borderBottom: "none",
            padding: "2px 2px",
          },
          [`& .${gridClasses.row}`]: {
            backgroundColor: ({ palette: { white } }) => white.main,
            "&:hover": {
              backgroundColor: ({ palette: { grey } }) => grey[100],
            },
            minHeight: "48px !important",
          },
          [`& .${gridClasses.columnHeaders}`]: {
            backgroundColor: ({ palette: { grey } }) => grey[50],
            borderRadius: 1,
            minHeight: "48px !important",
          },
          [`& .${gridClasses.footerContainer}`]: {
            display: "none",
          },
        }}
        pageSize={10}
        disableSelectionOnClick
        disableColumnMenu
        hideFooter={comunicaciones.length <= 10}
        components={{
          NoRowsOverlay: () => (
            <Box
              py={6}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <SendIcon sx={{ fontSize: 40, color: "#bdbdbd", mb: 2 }} />
              <SuiTypography variant="body2" color="textSecondary">
                No hay comunicaciones registradas para este proceso.
              </SuiTypography>
            </Box>
          ),
          LoadingOverlay: () => (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ),
        }}
      />

      {/* Menú de opciones para comunicaciones por EMAIL */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {(roles.isAdmin || roles.isCartera || roles.isOperador || roles.isDirector) && (
          <>
            <MenuItem
              onClick={() => handleOpenNotificar(comunicaciones.find((a) => a.id === selectedId))}
            >
              <SendIcon fontSize="small" sx={{ mr: 1 }} /> Notificar
            </MenuItem>
            <MenuItem
              onClick={() =>
                handleOpenUploadDialog(comunicaciones.find((a) => a.id === selectedId))
              }
            >
              <UploadFileIcon title="Balance" fontSize="small" sx={{ mr: 1 }} />
              Subir Archivo
            </MenuItem>
            <MenuItem
              onClick={() =>
                handleViewPdf(
                  "anexo_correo",
                  comunicaciones.find((a) => a.id === selectedId)
                )
              }
            >
              <PictureAsPdfIcon title="Ver PDF" fontSize="small" sx={{ mr: 1 }} />
              Ver Anexo Correo
            </MenuItem>
          </>
        )}
      </Menu>

      <EditComunicacion
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        comunicacion={selectedComunicacion}
        onSuccess={() => {
          fetchComunicaciones();
          setOpenEditDialog(false);
        }}
      />
      {/* Diálogo para ver destinatarios */}
      <DialogDestinatarios
        open={openDialog}
        onClose={handleCloseDialog}
        comunicacion={selectedComunicacion}
      />
      <NotificarComunicacion
        open={openDialogNotificar}
        onClose={handleCloseDialogNotificar}
        comunicacion={selectedComunicacion}
        onSuccess={handleNotificacionSuccess}
      />

      {/* Visor de PDF */}
      <FileDialog
        open={showPdfViewer}
        onClose={() => setShowPdfViewer(false)}
        title={pdfTitle}
        content={
          <embed
            src={pdfUrl}
            type="application/pdf"
            width="100%"
            height="500px"
            style={{ border: "none" }}
          />
        }
      />

      {/* Diálogo para subir archivos */}
      <Dialog
        open={openUploadDialog}
        onClose={() => !fileUploading && setOpenUploadDialog(false) && setUploadFile(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Subir Archivo</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <SuiTypography variant="body2" component="p">
              Tipo Archivo
            </SuiTypography>
            <Select
              value={tipoArchivo}
              onChange={(e) => setTipoArchivo(e.target.value)}
              displayEmpty
              fullWidth
            >
              <MenuItem value="">Seleccione un Tipo de Archivo</MenuItem>
              <MenuItem value="anexo_correo">Anexo (PDF)</MenuItem>
              {selectedComunicacion?.tipo_comunicacion === "FISICA" && (
                <MenuItem value="comprobante_envio">Comprobante de Envio (PDF)</MenuItem>
              )}
            </Select>
          </Box>
          <Box border="1px dashed #bdbdbd" borderRadius={1} p={3} textAlign="center" mb={2}>
            <SuiTypography variant="body2" component="p">
              Seleccione el Archivo
            </SuiTypography>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              id="archivoComunicacion-upload"
              style={{ display: "none" }}
              onChange={handleFileChange}
              disabled={fileUploading}
            />
            <label htmlFor="archivoComunicacion-upload">
              <Button
                component="span"
                variant="contained"
                startIcon={<UploadFileIcon />}
                disabled={fileUploading}
                sx={{ mb: 1 }}
              >
                Seleccionar archivo
              </Button>
            </label>

            {uploadFile && (
              <Box mt={2}>
                <SuiTypography variant="body2" color="primary">
                  Archivo seleccionado: {uploadFile.name}
                </SuiTypography>
              </Box>
            )}
          </Box>
          {tipoArchivo === "comprobante_envio" && (
            <SuiTypography variant="caption" color="textSecondary">
              El archivo debe ser un documento que compruebe el envío físico al destinatario (guía
              de envío, comprobante de empresa de mensajería, etc).
            </SuiTypography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUploadDialog(false)} disabled={fileUploading} color="error">
            Cancelar
          </Button>
          <SuiButton
            variant="gradient"
            buttonColor="dark"
            disabled={!uploadFile || fileUploading}
            onClick={handleUploadArchivo}
          >
            {fileUploading ? "Subiendo..." : "Subir Archivo"}
          </SuiButton>
        </DialogActions>
      </Dialog>
    </SuiBox>
  );
};

CardComunicaciones.propTypes = {
  proceso: PropTypes.object.isRequired,
  onProcesoChange: PropTypes.func,
};

export default CardComunicaciones;
